import Skeleton from "react-loading-skeleton";
import { type HoursResult, useStoreTime } from "~/hooks/hours";
import { useDispatchTypeLabel } from "~/hooks/language";
import { InfoIcon } from "~/icons";
import type { OrderDispatchOption } from "~/types";
import { Inline } from "../Inline/Inline";
import { Stack } from "../Stack/Stack";
import { Text } from "../Text/Text";

export function StoreHours({
  skeleton,
  type,
  hours,
}: {
  skeleton: boolean;
  type: "STORE" | OrderDispatchOption | null;
  hours: HoursResult | null;
}) {
  const { label: typeLabel, loading: loadingLabel } =
    useDispatchTypeLabel(type);
  const { formatTime, formatZone, formatTimeWithZone } = useStoreTime();

  if (skeleton || loadingLabel || hours?.loading) {
    return (
      <Stack space="1">
        <Text font="special" weight="strong" size="large">
          <Skeleton width={80} />
        </Text>
        <Text font="special" weight="strong" size="large">
          <Skeleton width={160} />
        </Text>
      </Stack>
    );
  }

  if (!hours) return null;

  const { from, to, status, message } = hours;

  const openTime = formatTimeWithZone(from);
  const closeTime = formatTimeWithZone(to);

  const openToClose =
    formatZone(from) === formatZone(to)
      ? `${formatTime(from)} to ${closeTime}`
      : `${openTime} to ${closeTime}`;

  return (
    <Stack space="1">
      {status === "now open" && (
        <NowOpen type={type} typeLabel={typeLabel} closeTime={closeTime} />
      )}

      {status === "closed" && (
        <Text
          font="special"
          weight="strong"
          size="large"
          color="jets-red"
          transform="uppercase"
        >
          {typeLabel} {status}
        </Text>
      )}

      {status === "closing soon" && (
        <Text
          font="special"
          weight="strong"
          size="large"
          color="warning"
          transform="uppercase"
        >
          {typeLabel} {status}
        </Text>
      )}

      <Text font="special" weight="strong" size="large">
        {status === "closed" ? `Opening ${openTime}` : openToClose}
      </Text>

      {Boolean(message) && (
        <Inline space="1" alignY={"top"}>
          <Text color="warning">
            <InfoIcon />
          </Text>
          <Text
            color="warning"
            font="special"
            size="large"
            weight="semi-strong"
          >
            {message}
          </Text>
        </Inline>
      )}
    </Stack>
  );
}

function NowOpen({
  type,
  typeLabel,
  closeTime,
}: {
  type: "STORE" | OrderDispatchOption | null;
  typeLabel: string | null;
  closeTime: string;
}) {
  switch (type) {
    case "STORE":
      return (
        <Text font="special" weight="strong" size="large" transform="uppercase">
          {typeLabel} hours - now open
        </Text>
      );

    case "DELIVERY":
    case "PICK_UP":
    case "SERVICE":
      return (
        <Text font="special" weight="strong" size="large" transform="uppercase">
          {typeLabel} available until {closeTime}
        </Text>
      );

    default:
      return null;
  }
}
